import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later, next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { EventData, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { uniq } from 'webapp/utils/common';

import willDestroy from 'webapp/modifiers/will-destroy';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonDemoFloatingButtonComponent from 'webapp/components/common/demo-floating-button';
import CommonSecurityInfoComponent from 'webapp/components/common/security-info';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import SourceConnectButtonComponent from 'webapp/components/source/connect/button';
import SubmitButtonComponent from 'webapp/components/submit-button';
import WizardConnectSourceComponent from 'webapp/components/wizard/connect-source';
import WizardDatasourceComponent from 'webapp/components/wizard/datasource';

import type { Metric, Source, SourceType } from 'webapp/models-custom';

import type { AfterConnectFn } from 'webapp/services/connect';
import { StepId } from 'webapp/services/wizard';

interface MetricsWithSources {
	sourceType?: SourceType;
	selectedMetrics: Metric[];
}

type Args = Record<string, never>;

export default class WizardConnectComponent extends Component<Args> {
	@service('wizard') declare wizardService: Service.Wizard;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('account') declare accountService: Service.Account;
	@service('connect') declare connectService: Service.Connect;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('logger') declare loggerService: Service.Logger;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked selectedSource: Source | null = null;
	@tracked selectedSources: number[];
	@tracked selectedMetricsWithSources: MetricsWithSources[];
	@tracked isCreating = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const { selectedMetricsBySourceKey, availableSources } = this.wizardService;

		this.selectedMetricsWithSources = Object.keys(selectedMetricsBySourceKey).map(
			(sourceKey) => {
				return {
					sourceType: availableSources.find((item) => item.key === sourceKey),
					selectedMetrics: selectedMetricsBySourceKey[sourceKey]!,
				};
			},
		);

		this.selectedSources = [];

		this.eventManagerService.on(AppEvent.SOURCE_CONNECTED, this, (source) => {
			next(this, () => {
				this.addNewConnectedSource(source);
			});
		});

		// restore selected source from service state when navigating back
		if (this.isQbOnly && this.wizardService.selectedSpecificSourceId) {
			const matchingSource = this.alreadyConnectedSourcesOfType.find(
				(source) => source.id === this.wizardService.selectedSpecificSourceId,
			);
			if (matchingSource) {
				this.selectSource(matchingSource);
			}
		}
	}

	get isQbOnly() {
		return this.wizardService.isQbOnlyFlow;
	}

	get selectedSourceTypeId() {
		return this.wizardService.selectedSourceType?.id;
	}

	get sourceInfo() {
		const sourceTypeId = this.selectedSourceTypeId;
		return sourceTypeId ? this.mmSourceTypesService.findModelById(sourceTypeId) : null;
	}

	get stepTitle() {
		return this.sourceInfo?.isFileUpload ? 'Upload your file' : 'Connect your data';
	}

	get selectedSpecificSourceId() {
		return this.selectedSource?.id || this.wizardService.selectedSpecificSourceId || '';
	}

	get alreadyConnectedSourcesOfTypeModels() {
		const sourceType = this.selectedSourceTypeId,
			sources = this.mmSourcesService.savedRecords.filter((s) => {
				return s.type === sourceType;
			});

		return sources;
	}

	get alreadyConnectedSourcesOfType() {
		return this.mmSourcesService.createModelsFromRecordsArray(
			this.alreadyConnectedSourcesOfTypeModels,
		);
	}

	get alreadyConnectedSources() {
		return this.alreadyConnectedSourcesOfType.filter((source) => source.canChooseOnOnboarding);
	}

	get showNextButton() {
		return (
			(this.isQbOnly && this.alreadyConnectedSources.length > 0) ||
			this.selectedSources.length > 0
		);
	}

	get allSourcesConnected() {
		return this.selectedSources.length === this.allConnectableSourcesCount;
	}

	get allConnectableSourcesCount() {
		const selectedMetricsWithSources = this.selectedMetricsWithSources;

		if (!selectedMetricsWithSources) {
			return 0;
		}

		return Object.keys(selectedMetricsWithSources).length;
	}

	get isNextButtonDisabled() {
		return this.isQbOnly && !this.selectedSpecificSourceId;
	}

	get nextButtonText() {
		let text;
		if (this.selectedSources.length) {
			text =
				(this.allSourcesConnected ? '' : 'Skip and') +
				' Continue <ui-icon name="ChevronRight" size="sm"></ui-icon>';
		} else {
			text = 'Select';
		}

		return text;
	}

	get isSecurityInfoShown() {
		const selectedMetricsWithSources = this.selectedMetricsWithSources,
			alreadyConnectedSourceTypes = uniq(
				this.mmSourcesService.savedRecords.map((item) => item.type),
			);

		if (!selectedMetricsWithSources) {
			return false;
		}

		const selectedMetricsWithSourceTypes: number[] = selectedMetricsWithSources
			.map((metric) => metric.sourceType?.id)
			.filter((id): id is number => id !== null && id !== undefined);

		const sourcesToChoose = [],
			sourcesToConnect = [];
		selectedMetricsWithSourceTypes.forEach((selectedMetricsWithSourceType) => {
			if (alreadyConnectedSourceTypes.includes(selectedMetricsWithSourceType)) {
				sourcesToChoose.push(selectedMetricsWithSourceType);
			} else {
				sourcesToConnect.push(selectedMetricsWithSourceType);
			}
		});

		return !!sourcesToConnect.length && !sourcesToChoose.length;
	}

	get nextButtonTheme() {
		let theme = 'blue';

		if (this.selectedSources.length && !this.allSourcesConnected) {
			theme = 'blue-outlined';
		}

		return theme;
	}

	get conectedSourcesCountText() {
		return this.selectedSources.length + '/' + this.allConnectableSourcesCount;
	}

	// showing also when first and only source is already connected
	get showMultipleSourcesSecurityText() {
		const selectedMetricsWithSourcesLength = this.selectedMetricsWithSources?.length || 0;
		return selectedMetricsWithSourcesLength > 1 || this.allSourcesConnected;
	}

	get singleSourceConnectHelp() {
		return this.selectedMetricsWithSources?.[0]?.sourceType;
	}

	get showDemoButton() {
		return (
			this.accountService.isDemoButtonShown &&
			this.alreadyConnectedSources.length === 0 &&
			this.isQbOnly
		);
	}

	private addNewConnectedSource(source: EventData.ByName[AppEvent.SOURCE_CONNECTED]) {
		this.sourceSelected(source.id);
	}

	private async resolveSelectedMetricsForSources(sourceIds: number[]) {
		const metrics: Metric[] = [];

		await Promise.allSettled(
			sourceIds.map(async (sourceId) => {
				const sourceIdString = String(sourceId),
					sourceType = this.mmSourcesService.findSourceTypeForSourceId(sourceIdString);
				if (sourceType) {
					/*
					wizardService.selectedMetricsBySourceKey[genericSource.key] is an array of generic Metric-s.
					Before we can use them, we need to resolve them with the sourceId (output is different Metric item)
					*/

					const selectedMetrics =
						this.wizardService.selectedMetricsBySourceKey[sourceType.key]!;

					await Promise.allSettled(
						selectedMetrics.map(async (selectedMetric) => {
							if (selectedMetric.isGenericCustom) {
								const resolvedMetricData =
									await this.mmMetricsService.resolveGenericMetricWithSpecific(
										selectedMetric.key,
										sourceId,
									);
								if (resolvedMetricData) {
									const metric =
										this.mmMetricsService.getMetricForKeyAndSpaceAccessId(
											resolvedMetricData.key,
											resolvedMetricData.spaceAccessId,
										);
									if (metric) {
										metrics.push(metric);
									}
								}
							} else if (selectedMetric.isGenericBasic) {
								const metric =
									this.mmMetricsService.getMetricForKeyAndSpaceAccessId(
										selectedMetric.key,
										sourceId,
									);
								if (metric) {
									metrics.push(metric);
								}
							} else {
								metrics.push(selectedMetric);
							}
						}),
					);
				}
			}),
		);

		return metrics;
	}

	private async createBoard(sourceIds: number[]) {
		const selectedMetricsBySourceKey = this.wizardService.selectedMetricsBySourceKey || {},
			layer = this.infolayerService.loading('Building dashboard...');

		this.isCreating = true;

		const metrics = await this.resolveSelectedMetricsForSources(sourceIds);

		this.mmBoardsService
			.createBoardFromMetrics(metrics)
			.then((board) => {
				const boardModel = this.mmBoardsService.findModel(String(board.id));

				if (boardModel) {
					const datasources = Object.keys(selectedMetricsBySourceKey);

					let from = 'wizard_metrics';
					if (this.flowTourOnboardingService.isBannerShown) {
						from = 'guided_onboarding';
					}

					this.mmBoardsService.triggerDataboardCreated({ from, datasources }, boardModel);
				}

				this.analyticsService.event(TrackingEvent.DATABOARD_WIZARD_METRICS_SUCCESS);

				return new Promise<void>((resolve) => {
					later(() => {
						layer.ok('Dashboard successfully built!');
						this.wizardService.setCreatedBoard(board);
						void this.wizardService.goToNext();
						resolve();
					}, 2000); // more likely for all blocks to have arrived via pusher already
				});
			})
			.catch((error) => {
				this.loggerService.logError('[Wizard] Failed auto generating board', {
					error: JSON.stringify(error),
				});
				layer.close();
			})
			.finally(() => {
				this.isCreating = false;
			});
	}

	@action
	selectSource(source: Source) {
		this.selectedSource = source;

		if (this.isQbOnly) {
			this.wizardService.selectedSpecificSourceId = source.id;
		}
	}

	@action
	connectSource(sourceType: SourceType | null, afterConnect?: AfterConnectFn) {
		if (!sourceType) {
			return;
		}

		if (!afterConnect) {
			afterConnect = (isSuccess, source) => {
				if (isSuccess && source) {
					const alreadyConnectedSources = this.alreadyConnectedSources,
						sourceModel = alreadyConnectedSources.find(
							(item) => item.numericId === source.id,
						);

					if (sourceModel) {
						this.selectSource(sourceModel);
						this.next(); // auto select and go to next step (works for isQbOnly flow)
					}
				}
			};
		}

		this.wizardService.connectSource(sourceType.key, afterConnect);
	}

	@action
	openRequestModal(sourceType?: SourceType) {
		if (!sourceType) {
			return;
		}

		if (this.currentUserService.model?.isVerified) {
			sourceType.requestConnect();
		} else {
			this.connectService.openEmailVerificationInfoPopup();
		}
	}

	@action
	contactUs() {
		this.currentUserService.chatWithSupport();
	}

	@action
	next() {
		const selectedSource = this.selectedSource,
			selectedSources =
				this.isQbOnly && selectedSource
					? [selectedSource.numericId!]
					: this.selectedSources;

		if (selectedSources && selectedSources.length > 0) {
			if (this.wizardService.isQbOnlyFlow) {
				void this.wizardService.goToNext();
			} else {
				void this.createBoard(selectedSources);
			}
		} else {
			this.wizardService.goToStep(StepId.STARTING);
		}
	}

	@action
	back() {
		if (this.wizardService.isQbOnlyFlow) {
			this.wizardService.goToSources();
		} else {
			this.wizardService.goToPrevious();
		}
	}

	@action
	sourceSelected(sourceId: number) {
		if (!this.selectedSources.includes(sourceId)) {
			this.selectedSources = [...this.selectedSources, sourceId];
		}
	}

	@action
	triggerNextStep() {
		if (
			this.allSourcesConnected &&
			(this.flowTourOnboardingService.isBannerShown || this.allConnectableSourcesCount === 1)
		) {
			this.next();
		}
	}

	@action
	connectAnotherSource() {
		const sourceType = this.sourceInfo;
		if (sourceType) {
			this.connectSource(sourceType);
		}
	}

	@action
	teardownView(): void {
		this.eventManagerService.off(AppEvent.SOURCE_CONNECTED, this);
	}
	<template>
		<div class="step-connect-ct" {{willDestroy this.teardownView}}>

			<div class="wizard-title">

				<div class="h">
					<h2>{{this.stepTitle}}</h2>

					{{#if this.isQbOnly}}
						{{#if this.alreadyConnectedSources}}
							{{#if this.sourceInfo.isSheets}}
								<p>
									To start building your dashboard, choose from the
									{{this.sourceInfo.name}}
									files you’ve already connected, or add another connection.
								</p>
							{{else}}
								<p>
									You seem to already have some
									{{this.sourceInfo.name}}
									accounts connected. Please choose one to use on this dashboard.
								</p>
							{{/if}}
						{{else}}
							<p>
								This integration allows you to create custom metrics. We will guide
								you through a 3-step process.
								<br />
								{{#if this.sourceInfo.isFileUpload}}
									First, upload your file.
								{{else}}
									First, click on ‘Connect’ to establish a secure connection with
									the source you want to use.
								{{/if}}
							</p>
						{{/if}}
					{{else}}
						<p>
							Connect the data sources needed for your selected metrics. You can add
							more metrics later.
						</p>
					{{/if}}
				</div>

			</div>

			<div class="content">

				<div class="list">

					{{#if this.isQbOnly}}
						{{#if this.alreadyConnectedSources}}
							<div class="scroll">
								{{#each this.alreadyConnectedSources as |source|}}
									<WizardDatasourceComponent
										@model={{source}}
										@selectedId={{this.selectedSpecificSourceId}}
										@onSelect={{this.selectSource}}
									/>
								{{/each}}
								<a
									href="#"
									class="list-item btn connect"
									{{on "click" this.connectAnotherSource}}
								>
									<CommonUiIconComponent @name="Connect" @size="sm" />Want to add
									another data source?
								</a>
							</div>
						{{else}}
							<div class="main info">
								<AvatarOrInitialsComponent
									@name={{this.sourceInfo.name}}
									@avatar={{this.sourceInfo.avatar}}
								/>
								<div class="details">
									<h3 class="name">{{this.sourceInfo.name}}</h3>
									<p class="description">{{this.sourceInfo.description}}</p>
								</div>
								<SourceConnectButtonComponent
									@sourceType={{this.sourceInfo}}
									@onConnect={{fn this.connectSource this.sourceInfo}}
									@extraClass="next"
									@size="big"
									@noDelay={{true}}
									@theme="green"
								/>
							</div>
							<CommonSecurityInfoComponent />
							{{#if this.sourceInfo.showConnectHelp}}
								<div class="connect-request">
									<CommonUiIconComponent
										@name="UserAccess"
										@size="lg"
										@class="box-icon"
									/>
									<div class="box-content">
										<span>Don’t have login credentials for this data source?</span>
										<br />
										<a
											class="link link--secondary link--underline"
											role="button"
											{{on
												"click"
												(fn this.openRequestModal this.sourceInfo)
											}}
										>
											Send your colleague a connect request via email
										</a>
									</div>
								</div>
							{{/if}}
						{{/if}}
					{{else}}
						<div class="main-wrapper">
							{{#each this.selectedMetricsWithSources as |sourceMetrics|}}
								<WizardConnectSourceComponent
									@sourceType={{sourceMetrics.sourceType}}
									@selectedMetrics={{sourceMetrics.selectedMetrics}}
									@onConnect={{this.connectSource}}
									@onSourceSelected={{this.sourceSelected}}
									@onOpenRequestModal={{this.openRequestModal}}
									@onTriggerNextStep={{this.triggerNextStep}}
								/>
							{{/each}}
						</div>
						{{#if this.isSecurityInfoShown}}
							<CommonSecurityInfoComponent />
						{{/if}}
						{{#unless this.showMultipleSourcesSecurityText}}
							<div class="connect-request">
								<CommonUiIconComponent
									@name="UserAccess"
									@size="lg"
									@class="box-icon"
								/>
								<div class="box-content">
									<span>Don’t have login credentials for this data source?</span>
									<br />
									<a
										class="link link--secondary link--underline"
										role="button"
										{{on
											"click"
											(fn this.openRequestModal this.singleSourceConnectHelp)
										}}
									>
										Send your colleague a connect request via email
									</a>
								</div>
							</div>
						{{/unless}}
					{{/if}}

				</div>

				<div class="wizard-controls smart-center-pos">
					<div class="container">

						<div class="center-container">
							{{#if this.showNextButton}}
								{{#unless this.isQbOnly}}
									<div class="connected-counter">
										Connected data sources:
										<strong>{{this.conectedSourcesCountText}}</strong>
									</div>
								{{/unless}}
								<SubmitButtonComponent
									@text={{this.nextButtonText}}
									@extraClass="next"
									@size="big"
									@theme={{this.nextButtonTheme}}
									@noDelay={{true}}
									@isDisabled={{this.isNextButtonDisabled}}
									@submitInProgress={{this.isCreating}}
									@onTrigger={{this.next}}
								/>
							{{/if}}
						</div>

						<a role="button" class="btn back" {{on "click" this.back}}>
							<CommonUiIconComponent @name="ArrowLeft" @size="xs" />
							Go back
						</a>

					</div>
				</div>

			</div>

			{{#if this.showDemoButton}}
				<CommonDemoFloatingButtonComponent @trackingOrigin="onboarding" />
			{{/if}}

		</div>
	</template>
}
